import { get } from "lodash";
import { GadmFeature } from "./types";
import { POLYGON_STYLE, POLYGON_HOVER_STYLE } from "./mapConfig";

const HOVER_DURATION = 300; // ms

// Almacena el ID de animación activa por polígono para poder cancelarla
const activeAnimations = new WeakMap<google.maps.Polygon, number>();
// Almacena el timeout de mouseout para cancelarlo si vuelve el mouseover
const hoverTimeouts = new WeakMap<google.maps.Polygon, ReturnType<typeof setTimeout>>();

// Interpola fillOpacity entre dos valores durante HOVER_DURATION ms (a ~30fps)
const animateFill = (polygon: google.maps.Polygon, from: number, to: number) => {
  // Cancela animación previa si existe
  const prev = activeAnimations.get(polygon);
  if (prev) cancelAnimationFrame(prev);

  const start = performance.now();
  let skip = false;
  const step = (now: number) => {
    skip = !skip;
    if (skip) { activeAnimations.set(polygon, requestAnimationFrame(step)); return; }
    const t = Math.min((now - start) / HOVER_DURATION, 1);
    polygon.setOptions({ fillOpacity: from + (to - from) * t });
    if (t < 1) {
      activeAnimations.set(polygon, requestAnimationFrame(step));
    } else {
      activeAnimations.delete(polygon);
    }
  };
  activeAnimations.set(polygon, requestAnimationFrame(step));
};

// Convierte coordenadas GeoJSON [lng, lat] al formato { lat, lng } de Google Maps
const toLatLng = (coord: number[]) => ({ lat: coord[1], lng: coord[0] });

// Normaliza Polygon y MultiPolygon a un array uniforme de rings (number[][][])
const getCoordinateRings = (feature: GadmFeature): number[][][] => {
  const type = get(feature, "geometry.type");
  const coords = get(feature, "geometry.coordinates") as number[][][] | number[][][][];
  if (type === "MultiPolygon") {
    return (coords as number[][][][]).flatMap((poly) => poly);
  }
  return coords as number[][][];
};

export interface BuildOptions {
  map: google.maps.Map;
  features: GadmFeature[];
  onFeatureClick: (feature: GadmFeature) => void;
}

// Construye y monta los google.maps.Polygon en el mapa, devuelve la lista para poder limpiarlos
export const buildPolygons = ({ map, features, onFeatureClick }: BuildOptions): google.maps.Polygon[] =>
  features.flatMap((feature) =>
    getCoordinateRings(feature).map((ring) => {
      const polygon = new google.maps.Polygon({ paths: ring.map(toLatLng), ...POLYGON_STYLE, map });
      polygon.addListener("click", () => onFeatureClick(feature));
      polygon.addListener("mouseover", () => {
        // Cancela mouseout pendiente para evitar parpadeo al soltar drag
        const pending = hoverTimeouts.get(polygon);
        if (pending) { clearTimeout(pending); hoverTimeouts.delete(polygon); }
        polygon.setOptions({ strokeWeight: POLYGON_HOVER_STYLE.strokeWeight });
        animateFill(polygon, POLYGON_STYLE.fillOpacity, POLYGON_HOVER_STYLE.fillOpacity);
      });
      polygon.addListener("mouseout", () => {
        hoverTimeouts.set(polygon, setTimeout(() => {
          hoverTimeouts.delete(polygon);
          polygon.setOptions({ strokeWeight: POLYGON_STYLE.strokeWeight });
          animateFill(polygon, POLYGON_HOVER_STYLE.fillOpacity, POLYGON_STYLE.fillOpacity);
        }, HOVER_DURATION));
      });
      return polygon;
    })
  );

// Elimina del mapa todos los polígonos de la lista
export const clearPolygons = (polygons: google.maps.Polygon[]) => {
  polygons.forEach((p) => p.setMap(null));
};
